import React from 'react';
import { Link } from 'react-router-dom';
import '../../css/components/duels/TournamentCard.css';
import HrnbButton from "../controls/HrnbButton";

/**
 * Карточка турнира
 * @param {Object} props
 * @param {Object} props.tournament - Данные турнира
 * @param {boolean} [props.compact=false] - Компактный режим для отображения в профиле
 */
function TournamentCard({ tournament, compact = false }) {
    const {
        id,
        title,
        description,
        coverImageId,
        format,
        gameType,
        status,
        maxParticipants,
        currentParticipants,
        prizePool,
        prizePoolAmount,
        currency,
        startDate,
        registrationDeadline,
        organizer
    } = tournament;

    // Форматирование статуса
    const statusLabels = {
        REGISTRATION: 'Регистрация',
        UPCOMING: 'Скоро начнётся',
        ACTIVE: 'Активен',
        FINISHED: 'Завершён',
        CANCELLED: 'Отменён'
    };

    const statusColors = {
        REGISTRATION: '#22c55e',
        UPCOMING: '#3b82f6',
        ACTIVE: '#8b5cf6',
        FINISHED: '#6b7280',
        CANCELLED: '#ef4444'
    };

    // Форматирование формата
    const formatLabels = {
        SINGLE_ELIMINATION: 'Одиночное выбывание',
        DOUBLE_ELIMINATION: 'Двойное выбывание',
        ROUND_ROBIN: 'Круговая система',
        SWISS: 'Швейцарская система'
    };

    // Форматирование даты
    const formatDate = (dateString) => {
        if (!dateString) return '—';
        return new Date(dateString).toLocaleDateString('ru-RU', {
            day: 'numeric',
            month: 'long',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    // Форматирование призового фонда
    const formatPrizePool = () => {
        if (!prizePool) return '—';
        if (prizePoolAmount && currency) {
            return `${prizePoolAmount.toLocaleString('ru-RU')} ${currency}`;
        }
        const labels = {
            CASH: 'Денежный',
            POINTS: 'Очки',
            MIXED: 'Смешанный'
        };
        return labels[prizePool] || prizePool;
    };

    // Прогресс заполнения
    const fillPercentage = maxParticipants
        ? Math.round((currentParticipants / maxParticipants) * 100)
        : 0;

    // URL обложки
    const coverUrl = coverImageId
        ? `${process.env.REACT_APP_API_URL || 'https://api.hrnb.ru/api'}/files/${coverImageId}`
        : null;

    return (
        <div className={`tournament-card ${compact ? 'tournament-card--compact' : ''}`}>
            {coverUrl && !compact && (
                <div className="tournament-card__cover">
                    <img src={coverUrl} alt={title} className="tournament-card__image" />
                    <span 
                        className="tournament-card__status"
                        style={{ backgroundColor: statusColors[status] }}
                    >
                        {statusLabels[status] || status}
                    </span>
                </div>
            )}

            <div className="tournament-card__content">
                <h3 className="tournament-card__title">{title}</h3>
                
                {description && !compact && (
                    <p className="tournament-card__description">{description}</p>
                )}

                <div className="tournament-card__meta">
                    <div className="tournament-card__meta-item">
                        <span className="tournament-card__meta-label">Формат:</span>
                        <span className="tournament-card__meta-value">
                            {formatLabels[format] || format}
                        </span>
                    </div>
                    <div className="tournament-card__meta-item">
                        <span className="tournament-card__meta-label">Тип:</span>
                        <span className="tournament-card__meta-value">
                            {gameType === 'DUEL' ? 'Дуэль' : gameType === 'BATTLE' ? 'Баттл' : 'Фристайл'}
                        </span>
                    </div>
                    {!compact && (
                        <div className="tournament-card__meta-item">
                            <span className="tournament-card__meta-label">Призовой фонд:</span>
                            <span className="tournament-card__meta-value">
                                {formatPrizePool()}
                            </span>
                        </div>
                    )}
                </div>

                <div className="tournament-card__participants">
                    <div className="tournament-card__participants-info">
                        <span className="tournament-card__participants-count">
                            {currentParticipants} / {maxParticipants}
                        </span>
                        <span className="tournament-card__participants-label">
                            участников
                        </span>
                    </div>
                    <div className="tournament-card__progress">
                        <div className="tournament-card__progress-bar">
                            <div 
                                className="tournament-card__progress-fill"
                                style={{ width: `${fillPercentage}%` }}
                            />
                        </div>
                    </div>
                </div>

                {!compact && (
                    <div className="tournament-card__dates">
                        <div className="tournament-card__date-item">
                            <span className="tournament-card__date-label">Начало:</span>
                            <span className="tournament-card__date-value">
                                {formatDate(startDate)}
                            </span>
                        </div>
                        {registrationDeadline && status === 'REGISTRATION' && (
                            <div className="tournament-card__date-item">
                                <span className="tournament-card__date-label">Регистрация до:</span>
                                <span className="tournament-card__date-value">
                                    {formatDate(registrationDeadline)}
                                </span>
                            </div>
                        )}
                    </div>
                )}

                {organizer && !compact && (
                    <div className="tournament-card__organizer">
                        <span className="tournament-card__organizer-label">Организатор:</span>
                        <Link
                            to={`/profile/${organizer.profileId}`}
                            className="tournament-card__organizer-name"
                        >
                            {organizer.displayName}
                        </Link>
                    </div>
                )}

                <div className="tournament-card__actions">
                    <Link to={`/tournaments/${id}`}>
                        <HrnbButton variant="primary" size="sm">
                            Подробнее
                        </HrnbButton>
                    </Link>
                </div>
            </div>
        </div>
    );
}

export default TournamentCard;
